import { Injectable } from '@angular/core';
import { HighchartsThemeService } from '@frk/eds-components';
import { DropdownOption, ToggleOption, ToggleOptionWithPeriod } from '@types';
import { symbols } from '@utils/il8n/currency';
import { getHighchartsLocaleFormat } from '@utils/text/date-utils';
import { SeriesOptionsType } from 'highcharts';
import moment from 'moment';
import {
  IndexCustomPerformance,
  IndexLevels,
  IndexPerformance,
} from '../../types/model/indexes';
import {
  IndexesPerformanceBRConfig,
  USIndexPerformanceState,
  USIndexPerformanceTableBarChartRow,
  USIndexPerformanceTimeSeriesTableData,
  USIndexPerformanceView,
} from '../../types/us-index.type';
import { US_INDEX_PERFORMANCE_INITIAL_STATE } from '../us-index.config';
import find from 'lodash/find';
import { FundCumulativePerformanceHistory } from '@products/models/performance-history';
import {
  IndexDetailsPerformance,
  IndexDetailsPerformanceCurrent,
} from '../../types/model/index-details';
import cloneDeep from 'lodash/cloneDeep';
import { EMDASH } from '@products/utils/constants/product.constants';
import { parseNumber } from '@utils/text/number-utils';
import get from 'lodash/get';
import { Observable, Subject } from 'rxjs';

import { ResponsiveService } from '@frk/eds-components';
// Highcharts series options extended with custom properties.
export type CustomSeriesOptions = SeriesOptionsType & {
  min?: number;
  currencySymbol?: string;
  _colorIndex?: number;
};

type HistoricalData = IndexLevels | FundCumulativePerformanceHistory; // Add any other type of array data, if needed.

@Injectable({
  providedIn: 'root',
})
export class UsIndexPerformanceBaseService {
  protected state: USIndexPerformanceState = cloneDeep(
    US_INDEX_PERFORMANCE_INITIAL_STATE
  );
  protected usIndexPerformanceState$ = new Subject<USIndexPerformanceState>();
  protected brLabels: object;
  protected brConfig: IndexesPerformanceBRConfig;

  // Section header monthly/quarterly dates.
  protected monthlyAsOfDate: string;
  protected quarterlyAsOfDate: string;
  /**
   * Angular device detection
   */
  protected isHandheld: boolean;
  constructor(
    protected highchartsTheme: HighchartsThemeService,
    protected responsiveService: ResponsiveService
  ) {
    if (this.responsiveService) {
      this.responsiveService.isHandheld$().subscribe((isHandheld: boolean) => {
        this.isHandheld = isHandheld;
      });
    }
  }

  /**
   * Returns updated state as Observable.
   */
  getUsIndexPerformanceState$(): Observable<USIndexPerformanceState> {
    return this.usIndexPerformanceState$.asObservable();
  }

  /**
   * Fetch initial chart options.
   */
  protected fetchChartOptions(chartType = 'Line'): Highcharts.Options {
    return {
      title: {
        text: undefined,
      },
      series: [],
      chart: {
        spacingLeft: chartType === 'Line' ? 0 : undefined,
        spacingRight: chartType === 'Line' ? 0 : undefined,
        marginRight: chartType === 'Line' ? 20 : undefined,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis:
        chartType === 'Line'
          ? {
              type: 'datetime',
              gridLineDashStyle: 'LongDash',
              // COREWEB-2921 increase tickPixelInterval from default 100, to force extra width for labels
              // NB: new value found by trial and error, and doesn't seem to reflect the actual pixel width
              tickPixelInterval: 110,
              // WORKAROUND: COREWEB-1543 set hour/minute/second to day display
              dateTimeLabelFormats: {
                millisecond: '%e. %b',
                second: '%e. %b',
                minute: '%e. %b',
                hour: '%e. %b',
                day: "%b '%e",
                week: "%b '%e",
                month: "%b '%y",
              },
              labels: {
                style: {
                  color: this.highchartsTheme.themeFonts.colors.grey,
                  fontSize: this.isHandheld
                    ? this.highchartsTheme.themeFonts.size.small
                    : this.highchartsTheme.themeFonts.size.p,
                  fontFamily: this.highchartsTheme.themeFonts.fontFamily,
                },
              },
            }
          : {
              categories: [],
              labels: {
                style: {
                  color: this.highchartsTheme.themeFonts.colors.grey,
                  fontSize: this.isHandheld
                    ? this.highchartsTheme.themeFonts.size.small
                    : this.highchartsTheme.themeFonts.size.p,
                  fontFamily: this.highchartsTheme.themeFonts.fontFamily,
                },
              },
            },
      yAxis:
        chartType === 'Line'
          ? [
              {
                gridLineDashStyle: 'LongDash',
                labels: {
                  format:
                    this.getCurrencySymbol(this.state.config.currencyCode) +
                    '{value}',
                  style: {
                    color: this.highchartsTheme.themeFonts.colors.grey,
                    fontSize: this.isHandheld
                      ? this.highchartsTheme.themeFonts.size.small
                      : this.highchartsTheme.themeFonts.size.p,
                    fontFamily: this.highchartsTheme.themeFonts.fontFamily,
                  },
                },
                title: {
                  text: null,
                },
                minorTickInterval: 'auto',
              },
            ]
          : {
              gridLineDashStyle: 'LongDash',
              minTickInterval: 0.1,
              title: {
                text: null,
              },
              labels: {
                useHTML: true,
                style: {
                  color: this.highchartsTheme.themeFonts.colors.grey,
                  fontSize: this.isHandheld
                    ? this.highchartsTheme.themeFonts.size.small
                    : this.highchartsTheme.themeFonts.size.p,
                  fontFamily: this.highchartsTheme.themeFonts.fontFamily,
                },
                formatter() {
                  return `${this.value}.0%`;
                },
              },
            },
      legend: {
        enabled: false,
        verticalAlign: 'bottom',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        // COREWEB-1277 set to day precision
        // http://api.highcharts.com/highcharts/tooltip.dateTimeLabelFormats
        xDateFormat: getHighchartsLocaleFormat(),
        valueDecimals: 2,
      },
      plotOptions:
        chartType === 'Line'
          ? {
              series: {
                marker: {
                  enabled: false,
                },
              },
              line: {
                marker: {
                  enabled: false,
                },
              },
            }
          : {
              column: {
                maxPointWidth: 24,
              },
              series: {
                events: {
                  legendItemClick() {
                    return false;
                  },
                },
                animation: {
                  duration: 2000,
                  easing: 'easeIn',
                },
                dataLabels: {
                  enabled: false,
                },
              },
            },
    };
  }

  /**
   * Filter the historical feed as per the date range, if available, and return accordingly.
   * @param historicalData historical data from feed in descending order
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  protected getFilteredDataByDateRange(
    historicalData: HistoricalData[],
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): {
    filteredHistoricalData: HistoricalData[];
    fromDateStd: string;
    toDateStd: string;
  } {
    let filteredHistoricalData: HistoricalData[];

    const { fromDateStd, toDateStd } = this.calculateFromAndToDateStd(
      historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    // LOGIC: Filter the original data from state, as per 'from/to' standard date, by moment library.
    filteredHistoricalData = historicalData.filter(
      (historicalDataObj: HistoricalData) =>
        moment(historicalDataObj.asOfDateStd, 'YYYY-MM-DD').isBetween(
          fromDateStd,
          toDateStd,
          null,
          '[]'
        )
    );

    return {
      filteredHistoricalData,
      fromDateStd,
      toDateStd,
    };
  }

  /**
   * @param historicalData historical data from feed in descending order
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  protected calculateFromAndToDateStd(
    historicalData: HistoricalData[],
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): {
    fromDateStd: string;
    toDateStd: string;
  } {
    let fromDateStd: string;
    let toDateStd: string;

    if (selectedToggleRange === 'CUSTOM_RANGE' && toDate) {
      toDateStd = moment(toDate).format('YYYY-MM-DD');
    } else {
      // Assigned 'toDateStd' to be as of historical list first 'asOfDateStd' value.
      toDateStd = moment(historicalData[0]?.asOfDateStd).format('YYYY-MM-DD');
    }

    if (selectedToggleRange === 'CUSTOM_RANGE' && fromDate) {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        moment(fromDate).format('YYYY-MM-DD'),
        toDateStd
      );
    } else {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        historicalData[historicalData.length - 1].asOfDateStd,
        toDateStd
      );
    }

    return {
      fromDateStd,
      toDateStd,
    };
  }

  /**
   * Calculate from date value in standard format.
   * @param type the key of the DATE_RANGE_ENUM map
   * @param startDateStd start date standard
   * @param endDateStd end date standard
   */
  private calculateFromDateStd(
    type: string,
    startDateStd: string,
    endDateStd: string
  ): string {
    const startMoment = moment(startDateStd, 'YYYY-MM-DD');
    const endMoment = moment(endDateStd, 'YYYY-MM-DD');

    switch (type) {
      case 'CUSTOM_RANGE':
        // custom range could be anything up to the full inception range
        return startDateStd;
      case 'SINCE_INCEPTION':
        return startDateStd;
      case 'MTD':
        const startOfMonth: moment.Moment = endMoment.startOf('month');
        if (startOfMonth.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfMonth.format('YYYY-MM-DD');
      case 'QTD':
        const startOfQtr: moment.Moment = endMoment.startOf('quarter');
        if (startOfQtr.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfQtr.format('YYYY-MM-DD');
      case 'YTD':
        const startOfYear: moment.Moment = endMoment.startOf('year');
        if (startOfYear.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfYear.format('YYYY-MM-DD');
      default:
        const period = this.state.config.chartConfig.dateRangeEnum.get(type)
          .period;
        const fromDate = endMoment.subtract(period.value, period.key);
        if (fromDate.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return fromDate.format('YYYY-MM-DD');
    }
  }

  /**
   * Return UTC standard unix timestamp, when given std format day e.g., YYYY-MM-DD.
   * @param val date in format locale or YYYY-MM-DD
   * @returns UTC unix timestamp
   */
  protected formatStdDateToUnixTimeStamp(val: string): number {
    if (!val || val === '-') {
      return null;
    }

    const stdDateFormatMatch: RegExpMatchArray = val.match(
      /^([0-9]{4})-([0-9]{2})-([0-9]{2})/
    );

    if (!stdDateFormatMatch) {
      return null;
    }

    const year = Number(stdDateFormatMatch[1]);
    const month = Number(stdDateFormatMatch[2]) - 1;
    const day = Number(stdDateFormatMatch[3]);
    const date = Date.UTC(year, month, day, 0, 0);

    return date.valueOf();
  }

  /**
   * Return the currency code as special character against alphabet format.
   * @param currencyCode the currency code in three character string format (alphabet).
   */
  protected getCurrencySymbol(currencyCode: string): string {
    return symbols.get(currencyCode);
  }

  /**
   * Construct dropdown range options.
   * @param dateRangeEnum all available date range
   */
  constructDropdownRangeOptions(
    dateRangeEnum: Map<string, ToggleOptionWithPeriod>
  ): DropdownOption[] {
    return Array.from(dateRangeEnum.values())
      .filter(
        (dropdownOption: ToggleOptionWithPeriod) =>
          dropdownOption.value !== 'CUSTOM_RANGE'
      )
      .map((dropdownOption: ToggleOptionWithPeriod) => ({
        value: dropdownOption.value,
        label: this.brLabels[dropdownOption.text],
        link: '#',
      }));
  }

  /**
   * Get available performance views in toggle option array format.
   * @param isMonthlyDataPresent boolean flag for monthly performance data, default is true
   * @param isQuarterlyDataPresent boolean flag for quarterly performance data, default is true
   */
  getPerformanceViews(
    isMonthlyDataPresent: boolean = true,
    isQuarterlyDataPresent: boolean = true
  ): ToggleOption[] {
    const perfViewToggleoptions: ToggleOption[] = [];

    Object.values(USIndexPerformanceView).forEach((perfView) => {
      perfViewToggleoptions.push({
        text: this.brLabels[
          `us-index-perf-view-toggle-${perfView.toLowerCase()}`
        ],
        active: this.state.config.selectedPerformanceView === perfView,
        value: perfView,
        disabled:
          perfView === USIndexPerformanceView.MONTHLY
            ? !isMonthlyDataPresent // reciprocate the boolean flags, as if data is present then disabled will be false.
            : !isQuarterlyDataPresent,
      });
    });

    return perfViewToggleoptions;
  }

  /**
   * Get the annualized return as per the date range selected.
   * @param dateRange date range selected
   * @param indexPerformance daily performance from pds
   * @param indexCustomPerformance custom performance from pds
   */
  protected getAnnualizedReturnByTimePeriod(
    dateRange: string,
    indexPerformance: IndexPerformance,
    indexCustomPerformance: IndexCustomPerformance
  ): string {
    switch (dateRange) {
      case 'MTD':
        return indexPerformance.monthToDateReturn;
      case 'QTD':
        return indexPerformance.quarterToDateReturn;
      case 'YTD':
        return indexPerformance.yearToDateReturn;
      case 'ONE_YEAR':
        return indexPerformance.return1Year;
      case 'TWO_YEARS':
        return indexPerformance.return2Year;
      case 'THREE_YEARS':
        return indexPerformance.return3Year;
      case 'FIVE_YEARS':
        return indexPerformance.return5Year;
      case 'SEVEN_YEARS':
        return indexPerformance.return7Year;
      case 'TEN_YEARS':
        return indexPerformance.return10Year;
      case 'SINCE_INCEPTION':
        return indexPerformance.returnSinceInception;
      default:
        return indexCustomPerformance?.cumulativeReturn;
    }
  }

  /**
   * Highcharts tooltip render function.
   */
  protected pointFormatterProvider(): () => string {
    return function (): string {
      let baseLineData = parseFloat(this.series.data[0].y);

      // COREWEB-4911 : first point can be invalid
      if (!baseLineData) {
        const baseLineDataPoint = find(this.series.data, (dataPoint) => {
          return !!dataPoint.y;
        });
        if (baseLineDataPoint) {
          baseLineData = parseFloat(baseLineDataPoint.y);
        }
      }

      const pointValue = parseFloat(this.y).toFixed(2);

      const tooltipHtml = `
          <span style='font-size:14px;color:${this.series.color}'>\u25CF</span>
          <span>${this.series.name}: </span>
          <b>${this.series.userOptions.currencySymbol}${pointValue} </b>
        `;

      return tooltipHtml;
    };
  }

  /**
   * Create fund performance table headers and body as per the performance view selected.
   * @param indexDetailsPerformance fund performance monthly/quarterly
   * @param selectedPerformanceView selected performance view
   * @param timeperiodList timeperiod list with their labels accordingly
   * @param isTableHeadersDynamic boolean flag for dynamic table headers, default false
   * @param dynamicYears calculated dynamic years, to be passed when 'isTableHeadersDynamic' is true.
   */
  createPerformanceTable(
    indexDetailsPerformance: IndexDetailsPerformance,
    selectedPerformanceView: USIndexPerformanceView,
    timeperiodList: object,
    isTableHeadersDynamic: boolean = false,
    dynamicYears?: string[]
  ): USIndexPerformanceTimeSeriesTableData {
    const header: string[] = [];
    const body: string[] = [];

    // Iterate timeperiod list to generate table headers and body.
    Object.entries(timeperiodList).forEach((timeperiod, index) => {
      // Assign table headers.
      header.push(
        isTableHeadersDynamic
          ? dynamicYears[index]
          : this.brLabels[`us-index-timeperiod-${timeperiod[1]}`]
      );

      // Assign table body.
      body.push(
        USIndexPerformanceView.MONTHLY === selectedPerformanceView
          ? indexDetailsPerformance.monthEnd[0]?.[timeperiod[0]]
          : indexDetailsPerformance.quarterEnd[0]?.[timeperiod[0]]
      );
    });

    return {
      lable: this.brLabels[
        `us-index-perf-view-toggle-${selectedPerformanceView.toLowerCase()}`
      ],
      asOfDate:
        USIndexPerformanceView.MONTHLY === selectedPerformanceView
          ? indexDetailsPerformance.monthEnd[0]?.asOfDate
          : indexDetailsPerformance.quarterEnd[0]?.asOfDate,
      header,
      body,
    };
  }

  /**
   * Get chart data from annualized performance feed.
   * @param data annualized performance data
   * @param chartColumns list of fields from feed
   */
  protected convertTableToChartData(
    data: IndexDetailsPerformanceCurrent[],
    chartColumns: string[]
  ): USIndexPerformanceTableBarChartRow[] {
    const chartRow: USIndexPerformanceTableBarChartRow[] = [];

    Object.keys(data).forEach((key) => {
      const obj: IndexDetailsPerformanceCurrent = data[key];

      chartColumns.forEach((column) => {
        const colValueAsString: string = get(obj, column, null);
        let decimals: number;
        let colValueAsNumber: number;

        if (colValueAsString) {
          const stripDecimals = colValueAsString
            .toString()
            .match(/[,.]([0-9]+)%?$/);
          decimals = colValueAsString !== EMDASH ? stripDecimals[1].length : 0;
          colValueAsNumber = parseNumber(colValueAsString);

          chartRow.push({
            y: colValueAsNumber ? colValueAsNumber : null,
            decimals,
          });
        }
      });
    });

    return chartRow;
  }

  /**
   * Convert pds feed column info into highcharts series format.
   * @param chartData chart data containing column info
   */
  protected convertToHighchartsColumnSeries(
    chartData: USIndexPerformanceTableBarChartRow[]
  ): SeriesOptionsType[] {
    if (!chartData) {
      return [];
    }

    const label = this.state.config.chartConfig.seriesName
      ?.replace(/(\r\n|\n|\r)/gm, '')
      .replace(/<a([^>]+?)([^>]*?)>(.*?)<\/a>/gi, '');

    const series: CustomSeriesOptions = {
      name: label,
      type: 'column',
      _colorIndex: 0,
      data: chartData,
    };

    return [series];
  }
  /**
   * set the initial state of chart state when components are destroyed
   * As state is maintained while routing from one indices to another because service is on root level.
   * to overcome problem of pervoius state persistance issue  while changing indices component.
   */

  public setUSIndexPerformanceState(): void {
    this.state = cloneDeep(US_INDEX_PERFORMANCE_INITIAL_STATE);
  }
}
